/**
 * TestimonialBannerCarousel — displays ten pre-designed testimonial banner
 * images grouped 4-per-slide (2x2 grid) that auto-swipe every 5 seconds.
 * Images live at /app/frontend/public/testimonials/01.webp … 10.webp.
 * Admin can swap them by uploading same-named files or via the landing_banners
 * collection (position="testimonial").
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

// v=20260210 → cache-bust for Cloudflare edge on custom domain
const ASSET_V = "20260210a";
const DEFAULT_SLIDES = Array.from({ length: 10 }, (_, i) => ({
    id: `testimonial-${i + 1}`,
    image_url: `/testimonials/${String(i + 1).padStart(2, "0")}.webp?v=${ASSET_V}`,
    alt: `Family testimonial ${i + 1}`,
}));
const AUTO_MS = 5000;
const PER_SLIDE = 2;

// Group slides into chunks of PER_SLIDE
const chunk = (arr, n) => {
    const out = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
};

export function TestimonialBannerCarousel() {
    const { t } = useLang();
    const [slides, setSlides] = useState(DEFAULT_SLIDES);
    const [pageIdx, setPageIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        let cancel = false;
        api.get("/public/testimonial-banners")
            .then(({ data }) => {
                if (cancel) return;
                if (Array.isArray(data?.items) && data.items.length > 0) setSlides(data.items);
            })
            .catch(() => { /* keep defaults */ });
        return () => { cancel = true; };
    }, []);

    const pages = useMemo(() => chunk(slides, PER_SLIDE), [slides]);
    const total = pages.length;
    const go = (n) => setPageIdx(((n % total) + total) % total);

    useEffect(() => {
        if (paused || total < 2) return undefined;
        const timer = setInterval(() => setPageIdx((i) => (i + 1) % total), AUTO_MS);
        return () => clearInterval(timer);
    }, [paused, total]);

    const activePage = pages[pageIdx] || [];
    const preload = useMemo(() => slides.slice(0, Math.min(8, slides.length)), [slides]);

    return (
        <section
            id="family-testimonials"
            data-testid="landing-testimonial-banners"
            aria-label="Family testimonials"
            className="relative bg-[#EFEFEF] py-6 md:py-8 overflow-hidden"
        >
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-4">
                    <h2 className="mt-1 text-[22px] sm:text-[28px] md:text-[30px] font-extrabold tracking-[-0.02em] text-[#6D28D9] leading-[1.12]">
                        Families Across India are Our Trusted User
                    </h2>
                    <p className="mt-2 text-[13px] sm:text-[14px] text-[#4B5563]">
                        Real families - Real Life Stories &amp; Legacies Preserved Forever.
                    </p>
                </div>

                {/* Carousel — 1x2 row of 2 images per slide */}
                <div className="relative rounded-xl overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={pageIdx}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-2 gap-3 sm:gap-4"
                            data-testid={`testimonial-page-${pageIdx}`}
                        >
                            {activePage.map((s, i) => (
                                <div
                                    key={s.id}
                                    className="relative aspect-[5/4] rounded-lg overflow-hidden bg-white shadow-sm border border-white/60"
                                    data-testid={`testimonial-tile-${pageIdx}-${i}`}
                                >
                                    <img
                                        src={s.image_url}
                                        alt={s.alt}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        loading={pageIdx === 0 ? "eager" : "lazy"}
                                        draggable={false}
                                    />
                                </div>
                            ))}
                            {/* Pad the last page so grid stays a 1x2 row */}
                            {activePage.length < PER_SLIDE &&
                                Array.from({ length: PER_SLIDE - activePage.length }).map((_, i) => (
                                    <div
                                        key={`pad-${i}`}
                                        aria-hidden="true"
                                        className="relative aspect-[5/4] rounded-lg overflow-hidden bg-white/40 border border-white/40"
                                    />
                                ))}
                        </motion.div>
                    </AnimatePresence>

                    {/* Prev / Next arrows */}
                    {total > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => { setPaused(true); go(pageIdx - 1); setTimeout(() => setPaused(false), 6000); }}
                                aria-label="Previous testimonials"
                                data-testid="testimonial-prev"
                                className="absolute top-1/2 -translate-y-1/2 left-2 sm:left-3 h-9 w-9 rounded-full bg-white/90 border border-[#EDE9FE] text-[#6D28D9] inline-flex items-center justify-center shadow-md hover:bg-white z-10"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setPaused(true); go(pageIdx + 1); setTimeout(() => setPaused(false), 6000); }}
                                aria-label="Next testimonials"
                                data-testid="testimonial-next"
                                className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-3 h-9 w-9 rounded-full bg-white/90 border border-[#EDE9FE] text-[#6D28D9] inline-flex items-center justify-center shadow-md hover:bg-white z-10"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* Progress + counter */}
                <div className="mt-3 flex items-center justify-center gap-3" data-testid="testimonial-progress">
                    <div className="text-[11px] font-semibold text-[#6D28D9] tracking-wide tabular-nums">
                        {String(pageIdx + 1).padStart(2, "0")} <span className="text-[#A78BFA]">/ {String(total).padStart(2, "0")}</span>
                    </div>
                    <div className="w-40 h-1 rounded-full bg-[#EDE9FE] overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9]"
                            animate={{ width: `${((pageIdx + 1) / total) * 100}%` }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </div>
                </div>

                {/* Preload first 8 (first two pages) so page transitions are instant */}
                <div className="hidden">
                    {preload.map((p) => <img key={p.id} src={p.image_url} alt="" />)}
                </div>
            </div>
        </section>
    );
}

export default TestimonialBannerCarousel;
